import { getBimPlans } from "apis/procore/bimPlans";
import { getLocations } from "apis/procore/locations";
import { AsyncStatus } from "types/misc";
import { log } from "utils/log";

import type { MiniMapPayload } from "./minimapSlice";
import type { Level } from "./types";

const l = log.miniMap.utils;

export type FetchMiniMapDataArgs = {
    companyId: number;
    projectId: number;
    modelRevisionId: string;
};

const emptyPayload = (): MiniMapPayload => ({
    levels: [],
    drawings: {},
    modelABs: {},
    drawingABs: {},
    currentLevelId: null,
    currentDrawingId: null,
});

/**
 * Fetch and compose the mini map payload (levels, drawings, drawing/model
 * mappings) by chaining the Procore BIM endpoints (model revision → plans →
 * locations).
 *
 * Pure data function: no Redux dispatch, no React, no per-drawing image
 * fetching. Consumed by the `getMiniMapData` RTK Query endpoint on
 * `procoreApi`, which is responsible for resolving `modelRevisionId` (using
 * the `getModelRevisions` endpoint with `latest: true`). The slice picks up
 * the result via an extraReducers matcher on `getMiniMapData.matchFulfilled`.
 * Per-drawing image prefetch is handled by `useMiniMapData`.
 *
 * Filters drawings to only those with `use_for_2d_nav === true`.
 */
export async function fetchMiniMapData(args: FetchMiniMapDataArgs): Promise<MiniMapPayload> {
    const { companyId, projectId, modelRevisionId } = args;

    const plans = await getBimPlans({ companyId, projectId, modelRevisionId });
    l.debug("Fetched plans for mini map:", plans);
    const locationIds = plans.map((plan) => plan.bim_level.location_id);

    const locations = await getLocations({ companyId, projectId, locationIds });
    l.debug("Fetched locations for mini map:", locations);

    const data: MiniMapPayload = emptyPayload();
    const bimFileRevisionInstanceIdToDrawingABId: Record<string, string> = {};

    plans.forEach((plan) => {
        // Only sheets explicitly enabled for 2D navigation should appear
        // in the minimap. The "Use for 2D Navigation" / "Do not use for
        // 2D Navigation" actions flip `use_for_2d_nav` on the mapping
        // and trigger a refetch of plans/extended. Iterate once to
        // populate drawingABs, the instance -> drawingAB map, and a
        // per-plan enabled set that feeds the completedItems filter.
        const enabledNavInstanceIds = new Set<string>();
        plan.drawing_mappings.forEach((mapping) => {
            if (!mapping.use_for_2d_nav) {
                return;
            }
            enabledNavInstanceIds.add(mapping.bim_file_revision_instance_id);
            data.drawingABs[mapping.id] = {
                id: mapping.id,
                pointA: [mapping.point_a.x, mapping.point_a.y],
                pointB: [mapping.point_b.x, mapping.point_b.y],
                sheetDimensions: [mapping.sheet_dimensions.width, mapping.sheet_dimensions.height],
            };
            bimFileRevisionInstanceIdToDrawingABId[mapping.bim_file_revision_instance_id] = mapping.id;
        });

        const navItems =
            plan.drawing_group.drawing_group_items?.filter((item) =>
                enabledNavInstanceIds.has(item.bim_file_revision_instance_id),
            ) ?? [];

        if (navItems.length > 0) {
            const currentLevel = data.levels.find((lvl) => lvl.id === plan.bim_level.id);
            if (currentLevel) {
                const drawingIds = navItems.map((item) => item.id);
                currentLevel.drawingIds.push(...drawingIds);
            } else {
                const defaultName = "Unknown Level";
                const location = locations.find((loc) => loc.id === plan.bim_level.location_id);
                const name = location?.name || defaultName;
                const nodeName = location?.node_name || defaultName;
                const drawingIds = navItems.map((item) => item.id);
                const level: Level = {
                    id: plan.bim_level.id,
                    elevation: plan.bim_level.elevation,
                    name,
                    nodeName,
                    drawingIds,
                };

                data.levels.push(level);
            }
        }

        const modelAB = plan.model_mapping.mapping;
        data.modelABs[modelAB.id] = {
            id: modelAB.id,
            pointA: [modelAB.point_a.x, modelAB.point_a.y, modelAB.point_a.z],
            pointB: [modelAB.point_b.x, modelAB.point_b.y, modelAB.point_b.z],
        };

        navItems.forEach((drawing) => {
            const drawingABId = bimFileRevisionInstanceIdToDrawingABId[drawing.bim_file_revision_instance_id];
            data.drawings[drawing.id] = {
                id: drawing.id,
                name: drawing.file_info.bim_file.file_name,
                drawingData: {
                    url: drawing.file_info.bim_file_revision.download_url,
                    asyncState: { status: AsyncStatus.Initial },
                },
                drawingABId,
                modelABId: modelAB.id,
            };
        });
    });

    // Default current level/drawing to the first ones (arbitrary). The slice
    // matcher writes these into state on every fulfilled fetch, matching the
    // pre-RTK-Query behavior of `setMiniMapData` (which also reset on every
    // fetch). User selection persists across re-renders via `setCurrentLevel`
    // / `setCurrentDrawing` and is only reset by a fresh fulfilled action.
    data.currentLevelId = data.levels.length > 0 ? data.levels[0].id : null;
    if (data.currentLevelId) {
        const drawingIds = data.levels.find((lvl) => lvl.id === data.currentLevelId)?.drawingIds;
        if (drawingIds && drawingIds.length > 0) {
            data.currentDrawingId = drawingIds[0];
        }
    }

    return data;
}
